import { useMemo } from 'react'
import { KanbanBoard, Chip, type KanbanItem, type KanbanStage } from '@/components/ui'
import type { SupportStage, TicketSummary } from '../api/types'
import { accentForHex, CATEGORY_SHORT, CATEGORY_VARIANT, PRIORITY_VARIANT } from '../utils/meta'

/**
 * Dynamic-stage Kanban for the support board. Columns come from the server
 * `SupportStage[]`; column reorder persists server-side (stages are org-shared
 * config). Transitions are permissive (kanban buckets). SLA-breached tickets
 * get a red left accent (spec §9.2).
 */

interface CardItem extends KanbanItem {
  card: TicketSummary
}

export interface SupportKanbanProps {
  stages: SupportStage[]
  items: TicketSummary[]
  selectedId?: string
  onSelect: (card: TicketSummary) => void
  onMove: (card: TicketSummary, targetStageId: string) => void
  onReorder: (stageIds: string[]) => void
}

export function SupportKanban({
  stages,
  items,
  selectedId,
  onSelect,
  onMove,
  onReorder,
}: SupportKanbanProps) {
  const kanbanStages: KanbanStage[] = useMemo(
    () =>
      [...stages]
        .sort((a, b) => a.sequence - b.sequence)
        .map((s) => ({ id: s.id, label: s.name, accent: accentForHex(s.color) })),
    [stages],
  )

  const kanbanItems: CardItem[] = useMemo(
    () =>
      items
        .filter((c) => c.stage_id != null)
        .map((c) => ({ id: c.id, stageId: c.stage_id as string, card: c })),
    [items],
  )

  const colorByStage = useMemo(() => {
    const m: Record<string, string | null> = {}
    for (const s of stages) m[s.id] = s.color
    return m
  }, [stages])

  return (
    <KanbanBoard<CardItem>
      stages={kanbanStages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={() => true}
      onAdvance={(item, _from, to) => onMove(item.card, to)}
      onSelect={(item) => onSelect(item.card)}
      onStageReorder={(next) => onReorder(next)}
      renderCard={(item) => (
        <SupportCard
          card={item.card}
          stageColor={colorByStage[item.card.stage_id ?? '']}
        />
      )}
      scrollRestorationKey="support"
    />
  )
}

function SupportCard({
  card,
  stageColor,
}: {
  card: TicketSummary
  stageColor?: string | null
}) {
  // Server-chosen stage color surfaced as an inline custom property — exempt
  // from the tokens-only rule because it's user data.
  const style = stageColor
    ? ({ ['--stage-color' as string]: stageColor } as React.CSSProperties)
    : undefined
  const refs = card.sale_order_refs?.length
    ? card.sale_order_refs.join(', ')
    : null
  const assignees = [card.assigned_to_name, card.secondary_assigned_to_name]
    .filter(Boolean)
    .join(' + ')
  return (
    <div
      style={style}
      className={`flex flex-col gap-1.5 ${
        card.is_sla_breached ? '-ml-3 border-l-2 border-l-danger pl-2.5' : ''
      }`}
      title={card.is_sla_breached ? 'SLA breached' : undefined}
    >
      <div className="flex items-center gap-2">
        {card.is_sla_breached ? (
          <span className="text-role-metadata font-medium text-danger">SLA breached</span>
        ) : null}
        <Chip size="xs" variant={CATEGORY_VARIANT[card.category]}>
          {CATEGORY_SHORT[card.category]}
        </Chip>
        <Chip size="xs" variant={PRIORITY_VARIANT[card.priority]}>
          {card.priority}
        </Chip>
      </div>
      <div className="text-role-data-primary truncate" title={card.title}>
        {card.partner_name ? `${card.partner_name} — ` : ''}
        {card.title}
      </div>
      <div className="text-role-metadata text-tertiary truncate">
        {card.ticket_number}
        {refs ? ` · SO ${refs}` : ''}
        {card.lot_refs?.length ? ` · Lot ${card.lot_refs.join(', ')}` : ''}
      </div>
      <div className="flex items-center gap-2 text-role-metadata">
        {card.sla_due_at ? (
          <span className={card.is_sla_breached ? 'font-medium text-danger' : 'text-tertiary'}>
            SLA {formatDateTime(card.sla_due_at)}
          </span>
        ) : null}
        <span className="text-tertiary">{assignees || 'Unassigned'}</span>
        {card.source_message_index_id ? (
          <span className="text-tertiary" title="From email">📧</span>
        ) : null}
      </div>
      {card.carla_summary ? (
        <div className="line-clamp-2 text-role-metadata italic" title={card.carla_summary}>
          {card.carla_summary}
        </div>
      ) : null}
    </div>
  )
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
